/**
 * B2B / wholesale tier pricing.
 *
 * Each User has a `priceTier` ("retail" | "wholesale1" | "wholesale2" | "vip")
 * and each Product can have optional `wholesale1Price`, `wholesale2Price`,
 * and `vipPrice` overrides. Higher tiers fall back through the chain so
 * VIP without a vipPrice gets the wholesale1Price; wholesale2 without a
 * wholesale2Price gets the wholesale1Price; etc. The retail price is the
 * always-present floor.
 */

import { prisma } from "@/lib/prisma";

export const GIFT_WRAP_PRICE = 5;

export type PriceTier = "retail" | "wholesale1" | "wholesale2" | "vip";

type PriceLike = number | string | { toString(): string } | null | undefined;

export interface ProductPriceFields {
  price: PriceLike;
  wholesale1Price?: PriceLike;
  wholesale2Price?: PriceLike;
  vipPrice?: PriceLike;
}

export interface UserTierFields {
  priceTier?: string | null;
}

function toNum(v: PriceLike): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(v.toString());
  return Number.isFinite(n) ? n : null;
}

/**
 * Returns the effective price for the user's tier, falling back through
 * the chain when an override is missing.
 *
 * - retail → product.price
 * - wholesale1 → wholesale1Price ?? price
 * - wholesale2 → wholesale2Price ?? wholesale1Price ?? price
 * - vip → vipPrice ?? wholesale1Price ?? price
 */
export function getEffectivePrice(
  product: ProductPriceFields,
  user?: UserTierFields | null
): number {
  const retail = toNum(product.price) ?? 0;
  const w1 = toNum(product.wholesale1Price);
  const w2 = toNum(product.wholesale2Price);
  const vip = toNum(product.vipPrice);

  const tier = (user?.priceTier as PriceTier | undefined) || "retail";

  switch (tier) {
    case "wholesale1":
      return w1 ?? retail;
    case "wholesale2":
      return w2 ?? w1 ?? retail;
    case "vip":
      return vip ?? w1 ?? retail;
    case "retail":
    default:
      return retail;
  }
}

/**
 * Convenience: load both the product and user from the DB and return
 * the effective price. Useful inside checkout APIs where you only have
 * IDs to start with.
 */
export async function getEffectivePriceById(
  productId: string,
  userId: string | null | undefined
): Promise<number> {
  const product = await prisma.product.findUnique({
    where: { id: productId },
    select: {
      price: true,
      wholesale1Price: true,
      wholesale2Price: true,
      vipPrice: true,
    },
  });
  if (!product) return 0;

  let user: { priceTier: string } | null = null;
  if (userId) {
    user = await prisma.user.findUnique({
      where: { id: userId },
      select: { priceTier: true },
    });
  }

  return getEffectivePrice(product, user);
}

/**
 * Calculate margin (% profit on cost) for a product. Returns null if
 * costPrice is missing or zero.
 */
export function getMarginPercent(
  costPrice: PriceLike,
  sellPrice: PriceLike
): number | null {
  const cost = toNum(costPrice);
  const sell = toNum(sellPrice);
  if (!cost || cost <= 0 || !sell) return null;
  return Math.round(((sell - cost) / sell) * 100);
}
